import { mapKeys } from "lodash";
import { useState } from "react";
import { Col, Form } from "react-bootstrap";

import { form } from "../forms";
import Widget from "../forms/components/Widget";
import usePrepareReanalyzeData from "../modules/header/hooks/usePrepareReanalyzeData";
import { AnalysisStatusType } from "../modules/samples/@types";
import {
  DYNAMIC_INTEGRATIONS,
  RlcsErrors
} from "../modules/samples/components/reanalyze/reanalyze-form";
import { LAYOUT_TYPE } from "../modules/samples/components/reanalyze/reanalyze-form-input/ReanalyzeFormInput";
import ReanalyzeIntegrationsFormInput from "../modules/samples/components/reanalyze/reanalyze-integrations-form-input/ReanalyzeIntegrationsFormInput";
import ReanalyzeCrawlerInput from "../modules/samples/components/reanalyze/reanalyze-local-crawl-input/ReanalyzeLocalCrawlerInput";
import { INITIAL_RLCS_PARAMS } from "../modules/samples/components/reanalyze/reanalyze-popup/consts";
import InteractiveAnalysisInput from "../modules/samples/components/reanalyze/rlcs-configuration-input/InteractiveAnalysisInput";
import RlcsConfigurationInput, {
  RLCSParamChangeValue
} from "../modules/samples/components/reanalyze/rlcs-configuration-input/RlcsConfigurationInput";
import { settings } from "../shim/jsbridge";
import tcbase from "../shim/tcbase";
import { isLocaleAvailableForPlatform } from "../utils";
import Icon from "./Icon";
import { Button, Divider, RenderWhen, TooltipIcon } from "./index";
import ToggleLink from "./toggle-link/ToggleLink";
import Typography from "./typography/Typography";

const { consts } = tcbase ?? {};

const extractRLCSErrors = (errors: RlcsErrors) => ({
  execution_timeout: errors?.timeout
});

const extractInteractiveRLCSErrors = (errors: RlcsErrors) => ({
  execution_timeout: errors?.interactive_timeout
});

export type ValuesType = {
  url?: string;
  scraper?: string;
  platform?: string;
  geolocation?: string;
  locale?: string;
  rl_dynamic_analysis?: boolean;
  rlcs_interactive?: boolean;
  execution_timeout?: number;
  interactive_platform?: string;
  interactive_execution_timeout?: number;
  interactive_geolocation?: string;
  interactive_locale?: string;
  fileless: boolean;
  joe?: boolean;
};

type Props = {
  setShow: () => void;
  onSubmit: () => void;
  errors: RlcsErrors;
  values: ValuesType;
  onValueChange: (value: RLCSParamChangeValue) => void;
  isUrlDisabled: boolean;
  isTiCloudDisabled: boolean;
  isRLDynamicDisabled: boolean;
  isInteractiveDisabled: boolean;
  isSubmitButtonDisabled: boolean;
  rlcsAnalysisInfo: AnalysisStatusType;
  interactiveAnalysisInfo: AnalysisStatusType;
};

const SubmitUrlForm = ({
  setShow,
  onSubmit,
  errors,
  values,
  onValueChange,
  isUrlDisabled,
  isTiCloudDisabled,
  isRLDynamicDisabled,
  isInteractiveDisabled,
  isSubmitButtonDisabled,
  rlcsAnalysisInfo,
  interactiveAnalysisInfo
}: Props) => {
  const {
    allow_local_crawl: allowLocalCrawl,
    private_url_reputation: privateUrlReputation
  } = settings || {};
  const isLocalScraperSelected = values.scraper === "local";

  const handleDynamicAnalysisParamChange = (value: RLCSParamChangeValue) => {
    const platform = value?.platform ?? values.platform;

    onValueChange({
      ...value,
      ...(isLocaleAvailableForPlatform(platform)
        ? { locale: values.locale ?? INITIAL_RLCS_PARAMS.locale }
        : { locale: undefined })
    });
  };

  const [advancedAnalysisShown, setAdvancedAnalysisShown] = useState(
    Boolean(values.rl_dynamic_analysis)
  );
  const advancedAnalysisLabel = advancedAnalysisShown
    ? "Hide Advanced Analysis Options"
    : "Advanced Analysis Options";
  const handleAdvancedAnalysisClick = () =>
    setAdvancedAnalysisShown(prev => !prev);

  const { data: { dynamic_analysis } = {} } = usePrepareReanalyzeData();

  return (
    <Form
      className="vertical-form"
      onSubmit={e => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <Widget
        name="url"
        type="text"
        onChange={event => {
          onValueChange({ url: event.target.value });
        }}
        placeholder="Enter address, e.g. https://example.com"
        disabled={isUrlDisabled}
        label={
          <div className="flex mb-10">
            <Typography variant={"h5"}>URL</Typography>
            <TooltipIcon
              icon="info-circle"
              message="Each URL analysis includes third-party reputation checks,
               information about hosting domain and IP address, redirect paths,
               SSL certificates, screenshots, and DOM capture"
              className="url-upload-tooltip-icon"
            />
          </div>
        }
        vertical
        value={values.url}
        required
        errors={errors?.url ? [errors.url] : undefined}
        data-testid="submit-url-input-field"
      />

      <div className="mb-20">
        <ToggleLink
          linkText={advancedAnalysisLabel}
          handleClick={handleAdvancedAnalysisClick}
          isDisabled={values?.url?.length === 0}
        />
      </div>

      <RenderWhen condition={advancedAnalysisShown}>
        <RenderWhen condition={!!privateUrlReputation}>
          <p className="text-color-lighter">
            Per your organization's security configuration, results and network
            indicators won’t be shared with third-party providers.
          </p>
          <hr className={"my-20"} />
        </RenderWhen>
        <div className="d-flex mb-10">
          <Icon name="dynamic-analysis" style={{ marginRight: "5px" }} />
          <Typography variant="h5">Dynamic Analysis </Typography>
          <TooltipIcon
            icon="info-circle"
            message="Execute the sample in a secure virtual environment to observe
            runtime behavior, complementing static analysis by capturing dropped
            files, network activity (PCAP), memory strings, and screenshots."
            className="url-upload-tooltip-icon"
          />
        </div>
        <RlcsConfigurationInput
          isFileAnalysis={false}
          isSelected={Boolean(values.rl_dynamic_analysis)}
          params={{
            platform: values.platform,
            locale: values.locale,
            geolocation: values.geolocation,
            execution_timeout: values.execution_timeout
          }}
          errors={extractRLCSErrors(errors)}
          disabled={isLocalScraperSelected || isRLDynamicDisabled}
          analysisInfo={{
            enabled:
              consts.rl_dynamic_analysis_enabled && rlcsAnalysisInfo.enabled,
            display_status: rlcsAnalysisInfo.display_status,
            additional_information: rlcsAnalysisInfo.additional_information
          }}
          onSelect={() => {
            onValueChange({
              rl_dynamic_analysis: !values.rl_dynamic_analysis,
              ...(values.rl_dynamic_analysis
                ? { ...INITIAL_RLCS_PARAMS, execution_timeout: undefined }
                : {})
            });
          }}
          onParamChange={handleDynamicAnalysisParamChange}
        />
        <InteractiveAnalysisInput
          isFileAnalysis={false}
          isSelected={Boolean(values.rlcs_interactive)}
          params={{
            platform: values.interactive_platform,
            execution_timeout: values.interactive_execution_timeout
          }}
          errors={extractInteractiveRLCSErrors(errors)}
          disabled={isLocalScraperSelected || isInteractiveDisabled}
          analysisInfo={{
            enabled:
              consts.rl_dynamic_analysis_enabled &&
              interactiveAnalysisInfo.enabled,
            display_status: interactiveAnalysisInfo.display_status,
            additional_information:
              interactiveAnalysisInfo.additional_information
          }}
          onSelect={() =>
            onValueChange({ rlcs_interactive: !values.rlcs_interactive })
          }
          onParamChange={value => {
            const transformed = mapKeys(
              value,
              (_, key) => `interactive_${key}`
            );
            onValueChange(transformed);
          }}
        />

        <RenderWhen condition={settings.URL_INTEGRATIONS?.length > 0}>
          <section className="flex-shrink-0">
            {DYNAMIC_INTEGRATIONS.filter(({ name }) =>
              settings.URL_INTEGRATIONS.includes(name)
            ).map(({ name, label }) => (
              <ReanalyzeIntegrationsFormInput
                key={name}
                name={name}
                label={label}
                selected={!!values[name as keyof ValuesType]}
                onToggle={() =>
                  onValueChange({ [name]: !values[name as keyof ValuesType] })
                }
                analysisInfo={dynamic_analysis?.[name]}
                layout={LAYOUT_TYPE.ROW}
                disabled={isLocalScraperSelected}
              />
            ))}
          </section>
        </RenderWhen>

        <Divider className="my-30" />

        <div className="d-flex mb-10">
          <Typography variant="h5">URL Crawling</Typography>
          <TooltipIcon
            icon="info-circle"
            message="Crawling options are managed by your administrator and can
            be configured on Administration page under Configuration > URL Analysis"
            className="url-upload-tooltip-icon"
          />
        </div>

        <ReanalyzeCrawlerInput
          disabled={
            values.scraper === "local" || isTiCloudDisabled || !values.url
          }
          label={`Analyze Crawled Files ${allowLocalCrawl ? "(Cloud)" : ""}`}
          labelIcon="cloud"
          selected={values.scraper === "cloud"}
          onToggle={() => {
            if (values.scraper === "cloud") {
              onValueChange({
                scraper: undefined,
                fileless: true
              });
            } else {
              onValueChange({ scraper: "cloud", fileless: false });
            }
          }}
        />
        <RenderWhen condition={allowLocalCrawl}>
          <ReanalyzeCrawlerInput
            disabled={
              values.scraper === "cloud" || !allowLocalCrawl || !values.url
            }
            label="Analyze Crawled Files (Local, On-Device)"
            labelIcon="local"
            selected={values.scraper === "local"}
            onToggle={() => {
              if (values.scraper === "local") {
                onValueChange({
                  fileless: true
                });
              } else {
                onValueChange({
                  scraper: "local",
                  fileless: false,
                  rlcs_interactive: false,
                  rl_dynamic_analysis: false
                });
              }
            }}
            tooltipMessage="This option allows additional settings to be configured in the Admin Configuration. However, note that the machine will access the remote site through your network."
          />
        </RenderWhen>
      </RenderWhen>

      <hr className={"my-25"} />

      <Form.Group>
        <Col>
          <div className="url-upload-buttons-container">
            <div className="url-upload-buttons-right">
              <Button
                onClick={setShow}
                data-testid="submit-url-cancel-button"
                variant="secondary"
                style={{ marginRight: "5px" }}
              >
                CANCEL
              </Button>
              <Button
                type="submit"
                data-testid="submit-url-submit-button"
                variant="primary"
                disabled={isSubmitButtonDisabled}
              >
                SUBMIT
              </Button>
            </div>
          </div>
        </Col>
      </Form.Group>
    </Form>
  );
};

export default form(SubmitUrlForm);
